import { assumeRole } from '@/api';
import { authQueryKeys, orgQueryKeys } from '@/config/queryKeys';
import { ORGS_QUERY_GC_TIME, ORGS_QUERY_STALE_TIME } from '@/constants';
import { useRoleLabel } from '@/hooks/useRoleLabel';
import { useRoles } from '@/hooks/useRoles';
import type { AgGridPurgeResult } from '@/lib/agGridRegistry';
import { purgeAllAgGridCaches } from '@/lib/agGridRegistry';
import { getAllRoles } from '@/lib/roles';
import { fetchOrganizations } from '@/pages/Organizations/orgUtils';
import { useAppStore } from '@/stores/app';
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import type { Org, Role, User } from '@/types';
import { getErrorMessage, isUnauthorizedError } from '@/types/errors';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate, useRouter } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';

import { filterOrgs, isChildRoute, normalizePath } from './utils';

/**
 * Hook to fetch organizations with proper error handling and retry logic
 */
export function useOrganizations() {
  return useQuery<Org[]>({
    queryKey: orgQueryKeys.list(),
    queryFn: fetchOrganizations,
    staleTime: ORGS_QUERY_STALE_TIME,
    gcTime: ORGS_QUERY_GC_TIME,
    retry: (failureCount, error) => {
      // Don't retry on unauthorized errors
      if (isUnauthorizedError(error)) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// Not optimistic despite appearances: the cache write is in onSuccess, not onMutate, so it is a
// post-confirmation patch for seamless switching and needs no rollback.
export function useAssumeRole() {
  const queryClient = useQueryClient();
  // Reported back, not thrown: onSuccess runs inside query-core's unguarded await, so throwing a
  // purge failure would mark an already-committed role switch as failed.
  const lastPurge = useRef<AgGridPurgeResult>({ purged: 0, failed: 0 });

  const mutation = useMutation({
    mutationFn: assumeRole,
    onSuccess: async (_data, variables) => {
      // Manual cache update for seamless switching
      queryClient.setQueryData<User | null>(authQueryKeys.me(), oldUser => {
        if (!oldUser) return null;
        return {
          ...oldUser,
          assumed_roles: {
            ...oldUser.assumed_roles,
            [variables.org_id]: variables.role,
          },
        };
      });

      // Everything, deliberately: the key factory carries org scope in shapes no one predicate can
      // match (`orgs/<id>/…` paths, `['org-users', id]`, `['studies','search', id]`), and this must
      // cover at least what the purge below clears or a purged grid refetches from a fresh entry.
      // This also covers authQueryKeys.me(), so it is not invalidated separately.
      const invalidated = queryClient.invalidateQueries();

      // Statement order is the guarantee, not the await: invalidateQueries marks matching entries
      // synchronously, and the datasource entries are observer-less, so the promise only tracks
      // other queries' refetches. Purging here rather than after it keeps an in-flight getRows from
      // resolving into the entry first and clearing the invalidated flag the purge depends on.
      lastPurge.current = purgeAllAgGridCaches();

      await invalidated;
    },
  });

  return { ...mutation, lastPurge };
}

/**
 * Hook to handle organization switching logic including navigation
 */
export function useOrgSwitcher() {
  const { t: tOrgs } = useTranslation('orgs');
  const location = useLocation();
  const navigate = useNavigate();

  const [pendingOrg, setPendingOrg] = useState<Org | null>(null);

  const { setSelectedOrganization, setSelectedOrgId } = useAppStore(
    useShallow(state => ({
      setSelectedOrganization: state.setSelectedOrganization,
      setSelectedOrgId: state.setSelectedOrgId,
    })),
  );

  // Commit the org switch after navigation has completed. Navigation is
  // awaited in handleOrgNavigation before setPendingOrg is called, so by
  // the time this effect fires the route has already changed.
  useEffect(() => {
    if (pendingOrg) {
      setSelectedOrganization(pendingOrg);
      setSelectedOrgId(pendingOrg.id);
      toast.success(tOrgs('org_switched_success', { name: pendingOrg.name }));
      // oxlint-disable-next-line set-state-in-effect -- resets the one-shot trigger post-navigation, not derivable during render
      setPendingOrg(null);
    }
  }, [pendingOrg, setSelectedOrganization, setSelectedOrgId, tOrgs]);

  const handleOrgNavigation = useCallback(
    async (org: Org) => {
      const path = normalizePath(location.pathname);

      if (isChildRoute('/studies', path)) {
        await navigate({ replace: true, to: '/studies' });
        setPendingOrg(org);
      } else if (isChildRoute('/instrument_runs', path)) {
        await navigate({ replace: true, to: '/instrument_runs' });
        setPendingOrg(org);
      } else if (isChildRoute('/samples', path)) {
        await navigate({ replace: true, to: '/samples' });
        setPendingOrg(org);
      } else {
        setSelectedOrganization(org);
        setSelectedOrgId(org.id);
        toast.success(tOrgs('org_switched_success', { name: org.name }));
      }
    },
    [location.pathname, navigate, setSelectedOrganization, setSelectedOrgId, tOrgs],
  );

  return { handleOrgNavigation };
}

/** Frozen: the same instance is handed to every super-admin caller, so an in-place sort by one
 *  consumer would reorder every other role menu in the app. */
export const ALL_ROLES: readonly Role[] = Object.freeze([...getAllRoles()]);

/**
 * Roles the current user may assume in `orgId`. Platform super admins can assume any role in any
 * org, so their set is the full hierarchy rather than the per-org grant, which is often absent
 * for them. Shared by the org selector and the sidebar role switcher so the two can't disagree
 * about how many roles the user has.
 *
 * The assumed role is unioned in because `useRoles`' `allowed_roles` fallback is all-or-nothing:
 * a user whose grant map exists but lacks an entry for this org would otherwise read as having no
 * roles here, while the org selector's badge still renders their assumed one.
 */
export function useCurrentOrgAllowedRoles(orgId: string | undefined): readonly Role[] {
  const { allowedRolesByOrg, assumedRolesByOrg, isGlobalSuperAdmin } = useRoles();
  const isSuperAdmin = isGlobalSuperAdmin();

  return useMemo(() => {
    if (isSuperAdmin) return ALL_ROLES;
    if (!orgId) return [];

    const granted = allowedRolesByOrg[orgId] ?? [];
    const assumed = assumedRolesByOrg[orgId];
    return assumed && !granted.includes(assumed) ? [assumed, ...granted] : granted;
  }, [allowedRolesByOrg, assumedRolesByOrg, orgId, isSuperAdmin]);
}

export function useRoleSwitcher(selectedOrg: Org, onClose: () => void) {
  const { t: tOrgs } = useTranslation('orgs');
  const roleLabel = useRoleLabel();
  const { assumedRolesByOrg } = useRoles();
  const { mutateAsync: performAssumeRole, isPending: isAssumingRole, lastPurge } = useAssumeRole();
  const { handleOrgNavigation } = useOrgSwitcher();
  const navigate = useNavigate();
  const router = useRouter();
  const { referrerPath, clearReferrerPath } = useUnauthorizedReferrerStore(
    useShallow(s => ({
      referrerPath: s.referrerPath,
      clearReferrerPath: s.clearReferrerPath,
    })),
  );

  const handleSwitchRole = useCallback(
    async (org: Org, role: Role) => {
      const isCurrentOrg = org.id === selectedOrg.id;

      // Scoped to the mutation alone. Wrapping the navigation too let a later failure raise
      // "Failed to switch role" after the success toast had already claimed it worked.
      if (assumedRolesByOrg[org.id] !== role) {
        try {
          await performAssumeRole({ org_id: org.id, role });
        } catch (e) {
          console.error('Failed to switch role', e);
          // The backend says *why* it refused (role not granted, org not found). getErrorMessage
          // falls through to `err.message` before the generic string, so that fallback is rare.
          toast.error(getErrorMessage(e, tOrgs('org_selector_role_switch_failed')));
          return;
        }
        // The switch itself committed, but a grid that refused to purge is still painting the
        // previous role's rows — the exact staleness the registry exists to prevent, and worse
        // under a plain success toast than under no toast at all.
        if (lastPurge.current.failed > 0) {
          toast.warning(
            tOrgs('org_selector_role_switch_stale_grids', { count: lastPurge.current.failed, role: roleLabel(role) }),
          );
        } else {
          toast.success(tOrgs('org_selector_role_switch_success', { role: roleLabel(role) }));
        }
      }

      // Both navigations are awaited: an unawaited one rejects into an unhandled promise, and
      // clearReferrerPath() used to run while the redirect it belongs to was still in flight.
      try {
        if (!isCurrentOrg) {
          await handleOrgNavigation(org);
        }

        if (referrerPath && router.state.location.pathname === '/unauthorized') {
          await navigate({ to: referrerPath });

          // Keep the stored path if the destination guard bounced us straight back: navigate()
          // resolves either way, because the router follows a redirect thrown in beforeLoad.
          if (router.state.location.pathname !== '/unauthorized') clearReferrerPath();
        }
      } catch (e) {
        console.error('Role switched, but post-switch navigation failed', e);
        toast.error(tOrgs('org_selector_role_switch_nav_failed'));
      }

      onClose();
    },
    [
      selectedOrg.id,
      assumedRolesByOrg,
      roleLabel,
      performAssumeRole,
      lastPurge,
      handleOrgNavigation,
      tOrgs,
      onClose,
      referrerPath,
      clearReferrerPath,
      navigate,
      router.state.location.pathname,
    ],
  );

  return { handleSwitchRole, isAssumingRole };
}

/**
 * Hook to manage client-side org search/filter state.
 */
export function useOrgSearch(orgs: Org[]) {
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => filterOrgs(orgs, query), [orgs, query]);

  return { query, setQuery, filtered };
}
